import { Client, searchStore } from "@/store/search";
import { defineStore } from "pinia";
import axios from "axios";
import { ClientsApi } from "@/ api/ClientsApi";
import { WorkerApi } from "@/ api/WorkerApi";
import { TagsApi } from "@/ api/TagsApi";
import { CommonEntityRecord } from "@/models/interfaces/common";
import { AddressChainLevel, Tag } from "@/store/clients";
import { TagEntityType } from "@/models/etc/types";
import { AccessApi } from "@/ api/AccessApi";
import { workerStore } from "@/store/worker";
import { AddressInputContainer } from "@/models/interfaces/entity-settings";
import { auxStore } from "@/store/auxillary";
import { RequestAddressContainer } from "@/composables/useAddressRequest";
import { commonRequestCreds, financesStore } from "@/store/finances";
import {
  CounterpartyAddContainer,
  CounterpartyExtended,
} from "@/models/interfaces/finances/counterparty-models";

export const clientPageStore = defineStore({
  id: "clientPageStore",
  state: () =>
    ({
      isClientLoading: true,
      isClientContactsLoading: true,
      isClientContactsPartialLoading: false,
      canLoadNextContacts: true,
      accessDenied: false,
      client: null,
      contacts: null,
    } as ClientPage),
  actions: {
    async getClient(clientId: number) {
      try {
        this.isClientLoading = true;
        this.accessDenied = false;
        this.client = await ClientsApi.getClient(clientId);
        // await this.refreshAccessRules(clientId);
      } catch (error) {
        if (axios.isAxiosError(error) && error.response?.status === 490) {
          this.accessDenied = true;
          this.client = null;
        }
        console.error(error);
      } finally {
        this.isClientLoading = false;
      }
    },
    async getContactsForClient(model: ClientContactsRequest) {
      type ContactsPage = {
        Count: number;
        Groups: { Id: number; Name: string; Persons: Contact[] }[];
      };

      const isFresh = !model.toSkip;
      try {
        if (isFresh) {
          this.canLoadNextContacts = true;
          this.isClientContactsLoading = true;
        } else {
          if (!this.canLoadNextContacts) return;
          this.isClientContactsPartialLoading = true;
        }

        const response = (await ClientsApi.getContactsForClient(
          model,
        )) as unknown as ContactsPage;

        if (isFresh) {
          this.contacts = response as unknown as Contact[];
        } else {
          const prev = this.contacts as unknown as ContactsPage | null;
          const prevGroups = prev?.Groups ?? [];
          const byId = new Map(prevGroups.map((g) => [g.Id, g]));
          for (const group of response.Groups ?? []) {
            const existing = byId.get(group.Id);
            if (existing) {
              const existingIds = new Set(existing.Persons.map((p) => p.Id));
              for (const person of group.Persons ?? []) {
                if (!existingIds.has(person.Id)) existing.Persons.push(person);
              }
            } else {
              prevGroups.push(group);
              byId.set(group.Id, group);
            }
          }
          this.contacts = {
            Count: response.Count,
            Groups: prevGroups,
          } as unknown as Contact[];
        }

        const receivedCount = (response.Groups ?? []).reduce(
          (acc, g) => acc + (g.Persons?.length ?? 0),
          0,
        );
        const pageSize = model.count ?? 0;
        if (receivedCount === 0 || (pageSize && receivedCount < pageSize)) {
          this.canLoadNextContacts = false;
        }
      } catch (error) {
        console.error(error);
      } finally {
        this.isClientContactsLoading = false;
        this.isClientContactsPartialLoading = false;
      }
    },
    async refreshAccessRules(clientId: number) {
      try {
        const result = await AccessApi.getClientAccessRules(clientId);
        if (!this.client) return; // ← guard от null

        const worker = workerStore();
        const name = worker.worker?.Name;

        const matchedRule = result?.Container.SpecialRules?.find(
          (rule) => rule.Group?.Name === name,
        ) || '';
        
        if (matchedRule == '') return;

        const workerRules = Object.fromEntries(
          (matchedRule?.Rules ?? []).map((r) => [r.Name, r]),
        );

        this.client.NameAccess = workerRules["ClientName"]?.Value;
        this.client.AddressAccess = workerRules["ClientAddress"]?.Value;
        this.client.UnionAccess = workerRules["ClientUnion"]?.Value;
        this.client.FileAccess = workerRules["ClientFiles"]?.Value;
        this.client.AttachedWorkersAccess =
          workerRules["ClientAddedWorkers"]?.Value;
        this.client.RuleAccess = workerRules["ClientRules"]?.Value;
        this.client.JobAccess = workerRules["ClientJob"]?.Value;
        this.client.PersonAccess = workerRules["ClientPerson"]?.Value;
        this.client.ParameterAccess = workerRules["ClientParameters"]?.Value;
        this.client.AccessLevel = workerRules["ClientAccess"]?.Value;
        this.client.DeleteAccess = workerRules["ClientDelete"]?.Value;
        this.client.JobPreviousAccess = workerRules["ClientJobPrevious"]?.Value;
        this.client.CounterpartiesAccess =
          workerRules["ClientCounterparties"]?.Value;
          console.log(workerRules);
      } catch (e) {
        console.error(e);
      }
    },
    async attachClientToUnion(entityIdToAttach: number) {
      if (!this.client) return;
      await ClientsApi.attachClient(this.client.Id, entityIdToAttach);
      await this.getClient(this.client.Id);
    },
    async detachClientFromUnion(entityIdToAttach: number) {
      if (!this.client) return;
      await ClientsApi.detachClient(this.client.Id, entityIdToAttach);
      await this.getClient(this.client.Id);
    },
    async attachWorkerToClient(workerId: number) {
      if (!this.client) return;
      await WorkerApi.attachWorker(this.client.Id, workerId);
      await this.getClient(this.client.Id);
    },
    async detachWorkerFromClient(workerId: number) {
      if (!this.client) return;
      await WorkerApi.detachWorker(this.client.Id, workerId);
      await this.getClient(this.client.Id);
    },
    async attachRoleToWorker(
      workerId: number,
      role: { Id: number; Name: string },
    ) {
      if (!this.client) return;
      await WorkerApi.attachRole(workerId, this.client.Id, role.Id);
      const worker = this.client.AttachedWorkers?.find(
        (w: any) => w.Id === workerId,
      );
      if (worker) {
        const roles = worker.Roles ?? [];
        if (!roles.some((r: any) => r.Id === role.Id)) {
          worker.Roles = [...roles, { Id: role.Id, Name: role.Name }];
        }
      }
    },
    async detachRoleFromWorker(workerId: number, roleId: number) {
      if (!this.client) return;
      await WorkerApi.detachRole(workerId, this.client.Id, roleId);
      const worker = this.client.AttachedWorkers?.find(
        (w: any) => w.Id === workerId,
      );
      if (worker?.Roles) {
        worker.Roles = worker.Roles.filter((r: any) => r.Id !== roleId);
      }
    },
    async attachTagToClient(tag: { Name: string }) {
      if (!this.client) return;
      const updatedTags = await TagsApi.addTag({
        EditingTagId: 0,
        Name: tag.Name,
        EntityType: TagEntityType.Client,
        EntityId: this.client.Id,
      });
      if (!Array.isArray(updatedTags)) return;
      this.client = { ...this.client, Tags: updatedTags };
    },
    async renameCurrentClient(name: string) {
      if (!this.client) return;
      await ClientsApi.renameClient(this.client.Id, name);
      this.client = { ...this.client, Name: name };
    },
    async updateCurrentClientAddress(container: AddressInputContainer) {
      if (!this.client) {
        return
      }
      await ClientsApi.setClientAddress(this.client.Id, container);
      // Локально проставляем значения по уровням в AddressComponents.
      const byLevel = new Map<number, string>(
        container.Components.map((c) => [c.Level, c.Value] as [number, string]),
      );
      // Если у клиента ещё не было адреса (AddressComponents пуст) — берём
      // структуру уровней из кеша auxStore (его уже загрузил сам селектор при
      // входе в редактирование), чтобы отображение не осталось пустым.
      const base: AddressNode[] = (this.client.AddressComponents?.length
        ? this.client.AddressComponents
        : (auxStore().getCachedAddressContainer as AddressNode[])) ?? [];
      this.client = {
        ...this.client,
        AddressComponents: base.map((node) => ({
          ...node,
          Value: byLevel.get(node.Level) ?? "",
        })),
      };
    },
    async deleteClient() {
      if (!this.client) return;
      try {
        this.isClientLoading = true;
        const deletedId = this.client.Id;
        await ClientsApi.deleteClient(deletedId);
        searchStore().removeClientFromList(deletedId);
      } catch (error) {
        this.isClientLoading = false;
        throw error;
      }
    },
    async detachTagFromClient(tagId: number) {
      if (!this.client) return;
      const ok = await TagsApi.deleteTag({
        TagId: tagId,
        EntityType: TagEntityType.Client,
        EntityId: this.client.Id,
      });
      if (!ok) return;
      this.client = {
        ...this.client,
        Tags: (this.client.Tags ?? []).filter((t) => t.Id !== tagId),
      };
    },
    updateCurrentClientFavouriteState(newState: boolean) {
      if (this.client) {
        this.client = {
          ...this.client,
          Favorite: newState,
        };
      }
    },
    /**
     * Локально переписывает список КА клиента ответом мутирующего запроса —
     * без перезагрузки всего клиента. В CounterpartyExtended нет IsDraft,
     * поэтому тянем его из текущего состояния по Id.
     */
    setClientCounterparties(
      list: CounterpartyExtended[],
      draftHints?: Record<number, boolean>,
    ) {
      if (!this.client) return;
      const prevDraft = new Map(
        (this.client.Counterparties ?? []).map((c) => [c.Id, c.IsDraft]),
      );
      this.client = {
        ...this.client,
        Counterparties: (list ?? []).map((cp: any) => ({
          Id: cp.Id,
          Name: cp.Name,
          Revenue: cp.Revenue ?? 0,
          Balance: cp.Balance ?? 0,
          Debt: cp.Debt ?? 0,
          Instance1C: cp.Instance1C ?? null,
          IsDraft: draftHints?.[cp.Id] ?? prevDraft.get(cp.Id) ?? false,
        })),
      };
    },
    /** Фолбэк, если мутирующий запрос не вернул обновлённый список КА. */
    async refreshClientCounterparties() {
      if (!this.client) return;
      const result = await financesStore().counterpartyGetAttachedList({
        Id: this.client.Id,
        ...commonRequestCreds(),
      });
      if (Array.isArray(result?.Counterparties)) {
        this.setClientCounterparties(result.Counterparties);
      }
    },
    async attachCounterparty(counterparty: { Id: number; IsDraft?: boolean }) {
      if (!this.client) {
        return;
      }
      const attached = await financesStore().counterpartyAddExisting({
        clientId: this.client.Id,
        counterpartyId: counterparty.Id,
      });
      if (!attached?.id) {
        await this.refreshClientCounterparties();
        return;
      }
      const mapped: Counterparty = {
        Id: attached.id,
        Name: attached.name,
        Revenue: attached.revenue ?? 0,
        Balance: attached.balance ?? 0,
        Debt: attached.debt ?? 0,
        Instance1C: attached.instance1C ?? null,
        IsDraft: attached.isDraft ?? false,
      };
      const list = this.client.Counterparties ?? [];
      const exists = list.some((c) => c.Id === mapped.Id);
      this.client = {
        ...this.client,
        Counterparties: exists
          ? list.map((c) => (c.Id === mapped.Id ? mapped : c))
          : [...list, mapped],
      };
    },
    /** Создаёт КА и сразу привязывает его к клиенту (Id в аскере — ид клиента). */
    async createCounterparty(container: CounterpartyAddContainer) {
      if (!this.client) return;
      const result = await financesStore().counterpartyAddNew({
        Id: this.client.Id,
        Container: container,
        ...commonRequestCreds(),
      });
      if (Array.isArray(result?.Counterparties)) {
        this.setClientCounterparties(result.Counterparties);
      } else {
        await this.refreshClientCounterparties();
      }
    },
    async detachCounterparty(counterpartyId: number) {
      if (!this.client) return;
      const { response_ok } = await financesStore().counterpartyDetach({
        Id: counterpartyId,
        ...commonRequestCreds(),
      });
      if (!response_ok) throw new Error("Не удалось отвязать контрагента");
      this.client = {
        ...this.client,
        Counterparties: (this.client.Counterparties ?? []).filter(
          (c) => c.Id !== counterpartyId,
        ),
      };
    },
  },
  getters: {
    clientLoading(state) {
      return state.isClientLoading;
    },
    clientContactsLoading(state) {
      return state.isClientContactsLoading;
    },
    clientContactsPartialLoading(state) {
      return state.isClientContactsPartialLoading;
    },
    clientContacts(state) {
      return state.contacts;
    },
    openedClient(state) {
      return state.client;
    },
  },
});

export type ClientPage = {
  isClientLoading: boolean;
  isClientContactsLoading: boolean;
  isClientContactsPartialLoading: boolean;
  canLoadNextContacts: boolean;
  accessDenied: boolean;
  client: ClientPageModel | null;
  contacts: Contact[] | null;
};

export type ClientContactsRequest = {
  tags?: number[] | null;
  toSkip?: number;
  searchStr: string;
  inputContainer?: RequestAddressContainer | null;
  count?: number;
  clientId?: number;
  searchType?: number | null;
};

export type Contact = {
  Id: number;
  Name: string;
  Post: string;
  Contacts: string;
  Clients: CommonEntityRecord[];
  Tags: Tag[];
  Changeable: boolean;
  MainInProject: boolean;
  AddressChain: AddressChainLevel[];
  CreateTime: string;
};

export type ClientPageModel = {
  CreateTime: "2020-08-14T19:13:26.368238+00:00";
  Creator: CommonEntityRecord;
  Favorite: boolean;
  Tags: CommonEntityRecord[];
  UnionClients: any[];
  AttachedWorkers: any[];
  Counterparties: Counterparty[];
  AddressComponents: AddressNode[];
  Folder: any;
  Parameters: ClientPageParam[];
  ShowClient1CValues: boolean;
  Revenue: number;
  Balance: number;
  Debt: number;
  ShowUnion1CValues: boolean;
  UnionRevenue: number;
  UnionBalance: number;
  UnionDebt: number;
  CanBeDeleted: boolean;

  NameAccess: number;
  AddressAccess: number;
  UnionAccess: number;
  FileAccess: number;
  AttachedWorkersAccess: number;
  RuleAccess: number;
  JobAccess: number;
  PersonAccess: number;
  ParameterAccess: number;
  AccessLevel: number;
  DeleteAccess: number;
  JobPreviousAccess: number;
  CounterpartiesAccess: number;

  Id: number;
  Name: string;
};

export type Counterparty = {
  Revenue: number;
  Balance: number;
  Debt: number;
  IsDraft: boolean;
  Instance1C: string | null;
  Id: number;
  Name: string;
};

export type AddressNode = {
  AddressNodeId: number;
  Placeholder: string;
  Required: boolean;
  Level: number;
  InBriefDisplay: boolean;
  Value: string;
};

export type ClientPageParam = {
  Type: number;
  Instance: {
    Unremovable: boolean;
    ValueType: number;
    StringValue: string | null;
    NumberValue: number | null;
    Round: number;
    BoolValue: boolean | null;
    ValueSet: any;
    SelectedValue: any;
    RecordAxisType: number;
    RecordFields: any;
    DateValue: string | null;
    ValueDisplayType: number;
    Id: number;
    Name: string;
  };
  ListTitle: any;
};
